import { useState } from 'react';
import { apiPost } from '../lib/api';
import { encryptSecret, generatePassphrase } from '../lib/vaultCrypto';

const EXPIRATIONS = [
  { label: '15 minutes', s: 15 * 60 },
  { label: '1 heure', s: 60 * 60 },
  { label: '24 heures', s: 24 * 60 * 60 },
  { label: '7 jours', s: 7 * 24 * 60 * 60 },
];

const MAX_VIEWS = [1, 3, 5, 10];

export function VaultCreatePage() {
  const [plaintext, setPlaintext] = useState('');
  const [passphrase, setPassphrase] = useState(() => generatePassphrase());
  const [expires, setExpires] = useState(EXPIRATIONS[1]!.s);
  const [maxViews, setMaxViews] = useState(1);
  const [recipientLabel, setRecipientLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ url: string; passphrase: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!plaintext.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const enc = await encryptSecret(plaintext, passphrase);
      const r = await apiPost<{ token: string }>('/api/vault/secrets', {
        ...enc,
        expires_in_seconds: expires,
        max_views: maxViews,
        burn_after_read: true,
        recipient_label: recipientLabel || undefined,
      });
      const url = `${window.location.origin}/s/${r.token}#${encodeURIComponent(passphrase)}`;
      setResult({ url, passphrase });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
        <h1 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 600 }}>🔐 Lien généré</h1>
        <p style={{ color: 'var(--text-mute)' }}>
          Le secret est chiffré dans votre navigateur. Le serveur ne peut pas le lire.
        </p>
        <div
          style={{
            background: 'var(--accent-soft)',
            border: '1px solid #bfdbfe',
            borderRadius: 8,
            padding: 14,
            fontSize: 'var(--fs-xs)',
            fontFamily: 'ui-monospace, Menlo, monospace',
            wordBreak: 'break-all',
            color: 'var(--accent-strong)',
            margin: '12px 0',
          }}
        >
          {result.url}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button className="btn primary" onClick={() => navigator.clipboard.writeText(result.url)}>
            Copier le lien
          </button>
          <button
            className="btn"
            onClick={() => {
              setResult(null);
              setPlaintext('');
              setPassphrase(generatePassphrase());
            }}
          >
            Nouveau secret
          </button>
        </div>
        <div
          style={{
            background: 'var(--warning-soft)',
            border: '1px solid var(--warning-border)',
            borderRadius: 6,
            padding: 12,
            fontSize: 'var(--fs-xs)',
            color: 'var(--warning-text)',
          }}
        >
          ⚠ Une fois cette page fermée, ni vous ni nous ne pouvons retrouver le contenu sans la passphrase.
          <br />
          Pour transmettre la passphrase séparément (Signal, téléphone), retirez le{' '}
          <code style={{ background: 'var(--warning-soft)', padding: '1px 4px', borderRadius: 3 }}>
            #{result.passphrase}
          </code>{' '}
          du lien.
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: 24 }}>
      <h1 style={{ fontSize: 'var(--fs-2xl)', fontWeight: 600 }}>🔐 Partager un secret en zero-knowledge</h1>
      <p style={{ color: 'var(--text-mute)' }}>
        Le secret est chiffré dans votre navigateur (AES-256-GCM, clé dérivée par PBKDF2-SHA256 600k itérations).
        Nous ne pouvons pas le lire, même si nous le voulions.
      </p>

      <div style={{ marginTop: 16 }}>
        <label style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>Contenu à partager</label>
        <textarea
          value={plaintext}
          onChange={(e) => setPlaintext(e.target.value)}
          placeholder="Mot de passe, clé API, certificat…"
          style={{
            width: '100%',
            minHeight: 120,
            padding: 10,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            fontFamily: 'ui-monospace, Menlo, monospace',
            fontSize: 'var(--fs-xs)',
            marginTop: 4,
            resize: 'vertical',
          }}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>
          Passphrase (à partager hors-bande avec le destinataire)
        </label>
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <input
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            style={{
              flex: 1,
              padding: 9,
              border: '1px solid var(--border-strong)',
              borderRadius: 6,
              fontFamily: 'ui-monospace, Menlo, monospace',
              fontSize: 'var(--fs-sm)',
            }}
          />
          <button className="btn" onClick={() => setPassphrase(generatePassphrase())} title="Regénérer">
            ↻
          </button>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 12, marginTop: 12 }}>
        <div>
          <label style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>Expiration</label>
          <select
            value={expires}
            onChange={(e) => setExpires(Number(e.target.value))}
            style={selectStyle}
          >
            {EXPIRATIONS.map((x) => (
              <option key={x.s} value={x.s}>{x.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>Vues max</label>
          <select
            value={maxViews}
            onChange={(e) => setMaxViews(Number(e.target.value))}
            style={selectStyle}
          >
            {MAX_VIEWS.map((n) => (
              <option key={n} value={n}>{n === 1 ? '1 (one-time)' : n}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)' }}>Étiquette destinataire (optionnel)</label>
        <input
          value={recipientLabel}
          onChange={(e) => setRecipientLabel(e.target.value)}
          placeholder="ex: Christian Lambert - Cimalpes"
          style={{
            width: '100%',
            padding: 8,
            border: '1px solid var(--border-strong)',
            borderRadius: 6,
            fontSize: 'var(--fs-sm)',
            marginTop: 4,
          }}
        />
      </div>

      {err && <div style={{ color: 'var(--danger)', fontSize: 'var(--fs-xs)', marginTop: 8 }}>{err}</div>}

      <button
        className="btn primary"
        onClick={submit}
        disabled={busy || !plaintext.trim() || !passphrase.trim()}
        style={{ marginTop: 16, padding: '10px 16px' }}
      >
        {busy ? '🔐 Chiffrement…' : '🔐 Chiffrer et générer le lien'}
      </button>
    </div>
  );
}

const selectStyle = {
  width: '100%',
  padding: 8,
  border: '1px solid var(--border-strong)',
  borderRadius: 6,
  fontSize: 'var(--fs-sm)',
  marginTop: 4,
  background: 'var(--bg)',
} as const;
